import { IntegrationParticleGeneratorState } from '@/components/integrations/particle-generator/Model';
import { ISourceOptions } from 'tsparticles-engine';

const getBaseConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  const baseConfig: ISourceOptions = {
    autoPlay: true,
    fpsLimit: 120,
    fullScreen: {
      enable: true,
      zIndex: -1
    }
  };

  if ((state.flowpage.enabled || state.section.enabled) && baseConfig.fullScreen) {
    baseConfig.fullScreen = {
      enable: false,
      zIndex: -1
    };
  }

  return baseConfig;
};

export const getSnowConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  const config: ISourceOptions = {
    ...getBaseConfig(state),
    particles: {
      move: {
        direction: 'bottom',
        enable: true,
        outModes: 'out',
        speed: 3
      },
      number: {
        density: {
          enable: true,
          area: 1600
        },
        value: 40
      },
      opacity: {
        value: 0.7
      },
      rotate: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: 20,
        animation: {
          enable: true,
          speed: 5,
          decay: 0,
          sync: false
        }
      },
      shape: {
        type: 'images',
        images:
          state && state.particle?.images
            ? state.particle.images.map((flagImage) => {
                return {
                  src: flagImage
                };
              })
            : undefined
      },
      size: {
        value: 8
      },
      wobble: {
        enable: true,
        distance: 5,
        speed: 10
      },
      zIndex: {
        value: { min: 0, max: 50 }
      }
    }
  };
  return config;
};

export const getConfettiConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  const config: ISourceOptions = {
    ...getBaseConfig(state),
    backgroundMask: {
      composite: 'destination-out',
      cover: {
        color: {
          value: '#fff'
        },
        opacity: 1
      },
      enable: false
    },

    detectRetina: true,
    duration: 0,
    fpsLimit: 30,
    interactivity: {
      detectsOn: 'window',
      modes: {
        slow: {
          factor: 3,
          radius: 200
        }
      }
    },
    particles: {
      bounce: {
        horizontal: {
          random: {
            enable: false,
            minimumValue: 0.1
          },
          value: 0
        },
        vertical: {
          random: {
            enable: false,
            minimumValue: 0.1
          },
          value: 0
        }
      },
      collisions: {
        bounce: {
          horizontal: {
            random: {
              enable: false,
              minimumValue: 0.1
            },
            value: 1
          },
          vertical: {
            random: {
              enable: false,
              minimumValue: 0.1
            },
            value: 1
          }
        },
        enable: false,
        mode: 'bounce',
        overlap: {
          enable: true,
          retries: 0
        }
      },
      color: {
        value: ['#cedc3c', '#ff9903', '#07978b', '#ff9903', '#cedc3c', '#4252b9', '#04bed7'],
        animation: {
          h: {
            count: 0,
            enable: true,
            offset: 0,
            speed: 30,
            decay: 0,
            sync: true
          },
          s: {
            count: 0,
            enable: false,
            offset: 0,
            speed: 1,
            decay: 0,
            sync: true
          },
          l: {
            count: 0,
            enable: false,
            offset: 0,
            speed: 1,
            decay: 0,
            sync: true
          }
        }
      },
      destroy: {
        mode: 'none',
        split: {
          count: 1,
          factor: {
            random: {
              enable: false,
              minimumValue: 0
            },
            value: 3
          },
          rate: {
            random: {
              enable: false,
              minimumValue: 0
            },
            value: {
              min: 4,
              max: 9
            }
          },
          sizeOffset: true
        }
      },
      groups: {},
      move: {
        angle: {
          offset: 0,
          value: 90
        },
        attract: {
          distance: 100,
          enable: false,
          rotate: {
            x: 100,
            y: 100
          }
        },
        center: {
          x: 50,
          y: 50,
          radius: 0
        },
        decay: 0.1,
        direction: 'top',
        enable: true,
        outModes: 'out',
        drift: 0,
        gravity: {
          acceleration: 4.81,
          enable: true,
          inverse: false,
          maxSpeed: 100
        },
        speed: {
          min: 5,
          max: 250
        }
      },
      number: {
        density: {
          enable: false,
          area: 800,
          factor: 1000
        },
        limit: 300,
        value: 0
      },
      opacity: {
        random: {
          enable: false,
          minimumValue: 0.1
        },
        value: 1,
        animation: {
          count: 0,
          enable: false,
          speed: 0.3,
          decay: 0,
          sync: true,
          destroy: 'min',
          startValue: 'max'
        }
      },
      reduceDuplicates: false,
      rotate: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: {
          min: 0,
          max: 360
        },
        animation: {
          enable: true,
          speed: 60,
          decay: 0,
          sync: false
        },
        direction: 'random',
        path: false
      },
      shadow: {
        blur: 0,
        color: {
          value: '#000'
        },
        enable: false,
        offset: {
          x: 0,
          y: 0
        }
      },
      size: {
        random: {
          enable: true,
          minimumValue: 2
        },
        value: 4
      },
      stroke: {
        width: 0
      },
      zIndex: {
        value: 0,
        opacityRate: 1,
        sizeRate: 1,
        velocityRate: 1
      },
      life: {
        count: 0,
        delay: {
          random: {
            enable: false,
            minimumValue: 0
          },
          value: 0,
          sync: false
        },
        duration: {
          random: {
            enable: false,
            minimumValue: 0.0001
          },
          value: 0,
          sync: false
        }
      },
      roll: {
        darken: {
          enable: true,
          value: 30
        },
        enable: true,
        enlighten: {
          enable: true,
          value: 30
        },
        mode: 'vertical',
        speed: {
          min: 15,
          max: 25
        }
      },
      tilt: {
        random: {
          enable: false,
          minimumValue: 0
        },
        value: {
          min: 0,
          max: 360
        },
        animation: {
          enable: true,
          speed: 60,
          decay: 0,
          sync: false
        },
        direction: 'random',
        enable: true
      },
      wobble: {
        distance: 30,
        enable: true,
        speed: {
          angle: {
            min: -15,
            max: 15
          },
          move: 10
        }
      }
    },
    pauseOnBlur: true,
    pauseOnOutsideViewport: true,
    zLayers: 100,
    emitters: {
      autoPlay: true,
      fill: true,
      rate: {
        quantity: 10,
        delay: 0.1
      },
      shape: 'square',
      startCount: 0,
      size: {
        mode: 'percent',
        height: 0,
        width: 0
      },
      position: {
        x: 50,
        y: 0
      }
    }
  };
  return config;
};

export const getFlagsConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  const config: ISourceOptions = {
    ...getBaseConfig(state),
    particles: {
      tilt: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: {
          min: 0,
          max: 5
        },
        animation: {
          enable: true,
          speed: 5,
          decay: 0,
          sync: false
        },
        direction: 'random',
        enable: true
      },
      move: {
        direction: 'bottom',
        enable: true,
        outModes: 'out',
        speed: 4
      },
      number: {
        density: {
          enable: true,
          area: 1600
        },
        value: 70
      },
      rotate: {
        random: {
          enable: true,
          minimumValue: 20
        },
        value: 0,
        animation: {
          enable: true,
          speed: 12,
          decay: 0,
          sync: false
        }
      },
      shape: {
        type: 'images',
        images:
          state && state.particle?.images
            ? state.particle.images.map((flagImage) => {
                return {
                  src: flagImage
                };
              })
            : undefined
      },
      size: {
        value: 12
      },
      wobble: {
        enable: true,
        distance: 5,
        speed: 10
      },
      zIndex: {
        value: { min: 0, max: 100 }
      }
    }
  };
  return config;
};

export const getCustomConfig = (state: IntegrationParticleGeneratorState): ISourceOptions => {
  const config: ISourceOptions = {
    ...getBaseConfig(state),
    particles: {
      color: { value: '#fff' },
      move: {
        direction: 'bottom',
        enable: true,
        outModes: 'out',
        speed: 2
      },
      number: {
        density: {
          enable: true,
          area: 1600
        },
        value: 50
      },
      rotate: {
        random: {
          enable: true,
          minimumValue: 0
        },
        value: 0,
        animation: {
          enable: true,
          speed: 5,
          decay: 0,
          sync: false
        }
      },
      shape: {
        type: 'images',
        images:
          state && state.particle?.images
            ? state.particle.images.map((flagImage) => {
                return {
                  src: flagImage
                };
              })
            : undefined
      },
      size: {
        value: 30
      },
      wobble: {
        enable: true,
        distance: 10,
        speed: 10
      },
      zIndex: {
        value: { min: 0, max: 100 }
      }
    }
  };
  return config;
};
